import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from 'app/services/header/header.service';
import { ToastrService } from 'ngx-toastr';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { readObservableError } from 'app/services/logiciel-caisse/logiciel-caisse-api.helper';
import { LogicielCaisseModuleService } from 'app/services/logiciel-caisse/logiciel-caisse-module.service';
import { AccesLogicielCaisseService } from 'app/services/logiciel-caisse/acces-logiciel-caisse.service';

@Component({
  selector: 'app-acces-logiciel-caisse-add',
  templateUrl: './acces-logiciel-caisse-add.component.html',
  styleUrls: ['./acces-logiciel-caisse-add.component.scss'],
})
export class AccesLogicielCaisseAddComponent implements OnInit {
  readonly steps = ['Entreprise', 'Identifiants', 'Validation'];
  currentStep = 0;
  isSubmitting = false;
  returnUrl = '/tiers/ticketCaisse/tous';

  code_station = '';
  erpStationId = 0;
  backStationId: number | null = null;
  enterpriseName = '';
  defaultEmail = '';

  email = '';
  password = '';
  confirmPassword = '';

  showPasswordError = false;
  showEmailError = false;

  /** Accès ERP déjà enregistré → connexion seulement (pas de nouvelle création). */
  hasExistingAcces = false;
  passwordLegacyHash = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly lcAuth: LogicielCaisseAuthService,
    private readonly moduleService: LogicielCaisseModuleService,
    private readonly accesService: AccesLogicielCaisseService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const ctx = this.lcAuth.getErpContext();
    if (!ctx) {
      void this.router.navigate(['/pages/authentication/login-v2']);
      return;
    }

    if (
      !this.moduleService.isEnabled(ctx.code_station) &&
      !this.moduleService.isEnabled(this.lcAuth.getEffectiveCodeStation(ctx.erpUserId))
    ) {
      void this.router.navigate(['/tiers/acces-logiciel-caisse/onboarding'], {
        queryParams: { returnUrl: this.route.snapshot.queryParamMap.get('returnUrl') },
      });
      return;
    }

    this.erpStationId = ctx.erpStationId;
    this.defaultEmail = ctx.email;
    this.enterpriseName = ctx.enterpriseName || '';
    this.email = ctx.email;
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/tiers/ticketCaisse/tous';
    this.code_station = this.lcAuth.getEffectiveCodeStation(ctx.erpUserId);

    this.headerService.setConfig({
      title: 'Configuration accès logiciel caisse',
      buttons: [],
    });

    this.accesService.getSyncStatus(ctx.code_station, ctx.erpStationId).subscribe({
      next: (status) => {
        const resolved = String(
          status.resolved_code_station || status.code_station || ctx.code_station
        ).trim();
        if (resolved) {
          this.code_station = resolved;
          this.moduleService.enableCodes(ctx.code_station, resolved);
        }

        this.hasExistingAcces = !!status.erp?.has_acces;
        this.passwordLegacyHash = !!status.password_legacy_hash;
        this.backStationId = status.back?.station_id ?? null;

        const rows = status.erp?.acces ?? [];
        const active = rows.find((r) => r.is_active !== false) ?? rows[0];
        if (active?.email) {
          this.email = active.email;
        }

        if (this.hasExistingAcces) {
          this.currentStep = 1;
          this.headerService.setConfig({
            title: 'Connexion logiciel caisse',
            buttons: [],
          });
        }

        if (status.sync_ok && this.lcAuth.isAuthenticated()) {
          void this.router.navigateByUrl(this.returnUrl);
        }
      },
      error: () => {
        this.accesService.listByCodeStation(this.code_station).subscribe({
          next: (rows) => {
            const active = rows.filter((r) => r.is_active !== false);
            const row = active[0] ?? rows[0];
            if (row?.email) {
              this.email = row.email;
              this.hasExistingAcces = true;
              this.currentStep = 1;
            }
          },
        });
      },
    });
  }

  retour(): void {
    void this.router.navigate(['/tiers/acces-logiciel-caisse/onboarding'], {
      queryParams: { returnUrl: this.returnUrl },
    });
  }

  onTimelineStepSelected(index: number): void {
    if (index <= this.currentStep) {
      this.currentStep = index;
    }
  }

  nextStep(): void {
    if (this.currentStep === 0) {
      this.currentStep = 1;
      return;
    }
    if (this.currentStep === 1) {
      if (!this.validateCredentialsStep()) {
        return;
      }
      this.currentStep = 2;
      return;
    }
    if (this.currentStep === 2) {
      void this.submit();
    }
  }

  private validateCredentialsStep(): boolean {
    this.showEmailError = !String(this.email || '').trim();
    this.showPasswordError =
      !String(this.password || '').trim() ||
      String(this.password).length < 6 ||
      this.password !== this.confirmPassword;

    if (this.showEmailError) {
      this.toastr.warning('Email requis.', 'Accès caisse');
      return false;
    }
    if (this.showPasswordError) {
      this.toastr.warning(
        this.password !== this.confirmPassword
          ? 'Les mots de passe ne correspondent pas.'
          : 'Mot de passe requis (6 caractères minimum).',
        'Accès caisse'
      );
      return false;
    }
    return true;
  }

  submit(): void {
    if (!this.validateCredentialsStep()) {
      return;
    }
    this.isSubmitting = true;

    const ctx = this.lcAuth.getErpContext();
    const email = String(this.email).trim().toLowerCase();
    const password = String(this.password);
    const code_station = String(this.code_station).trim();

    const onSuccess = () => {
      this.isSubmitting = false;
      this.toastr.success(
        this.hasExistingAcces
          ? 'Connexion logiciel caisse réussie.'
          : 'Configuration terminée. Accès logiciel caisse actif.',
        'Logiciel caisse'
      );
      void this.router.navigateByUrl(this.returnUrl);
    };

    const onError = (err: unknown) => {
      this.isSubmitting = false;
      this.toastr.error(readObservableError(err, 'Opération impossible.'), 'Erreur');
    };

    if (this.hasExistingAcces && !this.passwordLegacyHash) {
      this.lcAuth
        .loginWithCredentials(email, password, code_station, ctx ?? undefined, this.backStationId)
        .subscribe({ next: onSuccess, error: onError });
      return;
    }

    if (this.hasExistingAcces && this.passwordLegacyHash) {
      this.lcAuth
        .createAccesAndLogin({
          email,
          password,
          code_station,
          station_id: this.erpStationId || undefined,
          name: String(this.enterpriseName || '').trim() || undefined,
          station_nom: String(this.enterpriseName || '').trim() || undefined,
        })
        .subscribe({ next: onSuccess, error: onError });
      return;
    }

    this.lcAuth
      .createAccesAndLogin({
        email,
        password,
        code_station,
        station_id: this.erpStationId || undefined,
        name: String(this.enterpriseName || '').trim() || undefined,
        station_nom: String(this.enterpriseName || '').trim() || undefined,
      })
      .subscribe({ next: onSuccess, error: onError });
  }
}
